import cognitoAuth from '@/cognito'
import { S3Client, GetObjectCommand, PutObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3'
import { getSignedUrl } from '@aws-sdk/s3-request-presigner'
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers'

const AVATAR_PATH_PREFIX = 'profile-pictures/'

let awsConfigInitialized = false
let awsConfigInitializing = false
let initPromise = null

class Storage {
  constructor () {
    this.s3Client = null
    this.bucketName = process.env.VUE_APP_S3_BUCKET_FILE_UPLOAD_NAME
  }

  async _createS3Client () {
    if (awsConfigInitialized && this.s3Client) {
      return this.s3Client
    }

    const providerKey = `cognito-idp.${process.env.VUE_APP_AWS_REGION}.amazonaws.com/${process.env.VUE_APP_COGNITO_USER_POOL_ID}`
    const logins = {
      [providerKey]: async () => {
        const jwtToken = await cognitoAuth.getIdTokenPromise()
        if (!jwtToken) {
          throw new Error('No current user')
        }

        return jwtToken
      }
    }

    const credentials = fromCognitoIdentityPool({
      clientConfig: { region: process.env.VUE_APP_AWS_REGION },
      identityPoolId: process.env.VUE_APP_COGNITO_IDENTITY_POOL_ID,
      logins
    })

    return new S3Client({
      region: process.env.VUE_APP_AWS_REGION,
      credentials
    })
  }

  async init () {
    if (awsConfigInitialized) {
      if (!this.s3Client) {
        this.s3Client = await this._createS3Client()
      }
      return
    }

    if (awsConfigInitializing && initPromise) {
      await initPromise
      if (!this.s3Client) {
        this.s3Client = await this._createS3Client()
      }
      return
    }

    awsConfigInitializing = true
    initPromise = (async () => {
      this.s3Client = await this._createS3Client()
      awsConfigInitialized = true
      awsConfigInitializing = false
    })()

    await initPromise
  }

  async generatePresignedUrl (bucketName, key, operation, expires = 60 * 5) {
    if (!this.s3Client) {
      await this.init()
    }

    const bucket = bucketName || this.bucketName
    let command

    switch (operation) {
      case 'putObject':
        command = new PutObjectCommand({
          Bucket: bucket,
          Key: key,
          ContentType: 'application/octet-stream'
        })
        break
      case 'getObject':
        command = new GetObjectCommand({
          Bucket: bucket,
          Key: key,
          ResponseContentDisposition: `attachment; filename="${key.split('/').pop()}"`
        })
        break
      case 'deleteObject':
        command = new DeleteObjectCommand({
          Bucket: bucket,
          Key: key
        })
        break
      default:
        throw new Error(`Unsupported operation: ${operation}`)
    }

    try {
      const url = await getSignedUrl(this.s3Client, command, { expiresIn: expires })
      return url
    } catch (error) {
      console.error('Error generating presigned URL:', error)
      throw error
    }
  }

  async uploadFile (path, file) {
    if (!this.s3Client) {
      await this.init()
    }

    const uploadUrl = await this.generatePresignedUrl(
      this.bucketName,
      path,
      'putObject',
      60
    )

    try {
      await fetch(uploadUrl, {
        method: 'put',
        body: file
      })

      return {
        bucket: this.bucketName,
        key: path
      }
    } catch (e) {
      throw new Error(e.message)
    }
  }

  async getFile (key) {
    if (!this.s3Client) {
      await this.init()
    }

    const downloadUrl = await this.generatePresignedUrl(
      this.bucketName,
      key,
      'getObject',
      60 * 5
    )

    return downloadUrl
  }

  async deleteFile (key) {
    if (!this.s3Client) {
      await this.init()
    }

    const deleteUrl = await this.generatePresignedUrl(
      this.bucketName,
      key,
      'deleteObject',
      60
    )

    try {
      const response = await fetch(deleteUrl, {
        method: 'DELETE'
      })
      if (!response.ok) {
        throw new Error(`Failed to delete file: ${response.status} ${response.statusText}`)
      }
    } catch (e) {
      throw new Error(e.message)
    }
  }

  isAvatarPath (key) {
    return key && key.startsWith(AVATAR_PATH_PREFIX)
  }

  getAvatarCloudFrontUrl (key) {
    const domain = process.env.VUE_APP_CLOUDFRONT_AVATAR_DOMAIN

    if (!domain) {
      console.warn('VUE_APP_CLOUDFRONT_AVATAR_DOMAIN not configured for avatars')
      return null
    }

    if (!this.isAvatarPath(key)) {
      console.warn(
        `Key "${key}" is not an avatar path (${AVATAR_PATH_PREFIX}*). CloudFront URL only available for avatars.`)
      return null
    }

    return `https://${domain}/${key}`
  }

  extractKeyFromPresignedUrl (url) {
    try {
      if (!url) return null

      const urlWithoutQuery = url.split('?')[0]
      const urlObj = new URL(urlWithoutQuery)
      const path = urlObj.pathname

      return path.startsWith('/') ? path.substring(1) : path
    } catch (error) {
      console.error('Error extracting key from URL:', error)
      return null
    }
  }
}

Storage.region = process.env.VUE_APP_AWS_REGION

export default Storage
